// @erp/auth-kit/fe — the portal end: one /me query wrapped in a provider,
// claim-check sugar for affordances. Browser-only (solid export condition).
//
// useCan()/<Can> are UI SUGAR ONLY — they hide affordances; the API re-checks
// every call via rbac() (ADR-001 §14.2). Both ends share rbac/can(), so a
// grant the UI shows is exactly a grant the API enforces.
//
// CapabilitiesProvider/useCapabilities are the same shape over the INSTALL's
// capabilities manifest (ERP-DEPLOYMENT-MODEL §13): one /api/capabilities
// query, `has(module)`/`hasFeature(id)` sugar, `<HasModule>`/`<HasFeature>`
// gates. Sugar again — `/api/<m>/*` for a disabled module answers 404
// MODULE_DISABLED whatever the UI shows.
//
// Solid Query idioms per DEV-GUIDE-002 §3: options go in as a function; the
// result is a store — read q.data through accessors, never destructure.
import { createContext, useContext } from "solid-js";
import type { JSX, ParentProps } from "solid-js";
import { Show } from "solid-js";
import { useQuery } from "@tanstack/solid-query";
import { MODULE_IDS, hasFeature as capsHasFeature, hasModule as capsHasModule } from "@erp/core-schemas";
import type { Capabilities, ModuleId, Session } from "@erp/core-schemas";
import { ROLE_GRANTS, can } from "../rbac/index.js";
import type { Permission, RoleGrants } from "../rbac/index.js";

/**
 * The /me query key. Exported so callers invalidate/refetch through the
 * abstraction instead of hardcoding the tuple — a rename here can't silently
 * desync a screen's cache poke (DEV-GUIDE-002 §3.5: no ad-hoc string keys).
 */
export const SESSION_QUERY_KEY = ["auth", "me"] as const;

export interface SessionState {
  /** Claims when signed in, null when anonymous or still loading. */
  session: () => Session | null;
  /** True only during the initial /me round-trip. */
  loading: () => boolean;
  /**
   * True when /me errored past its retries (server unreachable) — distinct
   * from signed-out. The shell shows "can't reach the server", NOT the login
   * door, so a transient outage never boots a valid session (DEV-GUIDE-002 §3).
   */
  error: () => boolean;
  /** Claim-check against the provider's grant matrix. Anonymous → false. */
  can: (perm: Permission) => boolean;
  /** Re-fetch /me (after sign-in/sign-out mutations settle). Awaitable. */
  refetch: () => Promise<void>;
}

const SessionContext = createContext<SessionState>();

export interface SessionProviderProps {
  /**
   * The one /me call — typically `() => unwrap(client.auth.me.$get())`.
   * Resolve null for 401 (anonymous is a state, not an error).
   */
  fetchMe: () => Promise<Session | null>;
  /** Extended grant matrix — pass the same map the gateway's createRbac uses. */
  grants?: RoleGrants;
}

export function SessionProvider(props: ParentProps<SessionProviderProps>): JSX.Element {
  const q = useQuery(() => ({
    queryKey: SESSION_QUERY_KEY,
    queryFn: () => props.fetchMe(),
    staleTime: 60_000,
    // fetchMe maps 401 → null by contract, so anything that THROWS is
    // transient (network/5xx) — retry it briefly rather than booting a
    // validly signed-in user into an anonymous shell (DEV-GUIDE-002 §3.1's
    // portal default, minus the auth-error carve-out fetchMe already makes).
    retry: (count) => count < 2,
  }));

  const session = () => q.data ?? null;
  const state: SessionState = {
    session,
    loading: () => q.isPending,
    error: () => q.isError,
    can: (perm) => {
      const s = session();
      return s ? can(s, perm, props.grants ?? ROLE_GRANTS) : false;
    },
    refetch: async () => {
      await q.refetch();
    },
  };

  return <SessionContext.Provider value={state}>{props.children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession must be used inside <SessionProvider>");
  return ctx;
}

/** Accessor form for JSX conditions: `const canDesk = useCan('lib:desk'); <Show when={canDesk()}>`. */
export function useCan(perm: Permission): () => boolean {
  const ctx = useSession();
  return () => ctx.can(perm);
}

/** Affordance gate: renders children only when the claim check passes. */
export function Can(props: ParentProps<{ perm: Permission; fallback?: JSX.Element }>): JSX.Element {
  const ctx = useSession();
  return (
    <Show when={ctx.can(props.perm)} fallback={props.fallback}>
      {props.children}
    </Show>
  );
}

// ─── The install's capabilities manifest (§13) ─────────────────────────────

/**
 * The /api/capabilities query key. Exported for the same reason as
 * SESSION_QUERY_KEY: a cache poke names the abstraction, never the tuple.
 */
export const CAPABILITIES_QUERY_KEY = ["install", "capabilities"] as const;

export interface CapabilitiesState {
  /**
   * The manifest as the gateway sent it, or null when none is in hand — still
   * loading, or the UNRESTRICTED default (outside a provider / after an
   * error). Tell the two apart with `loading()`; decide with `has()` and
   * `hasFeature()`, which already encode the default.
   */
  capabilities: () => Capabilities | null;
  /** True only during the initial round-trip. The shell waits for it exactly as it waits for /me. */
  loading: () => boolean;
  /** Is this module part of the install? False while loading; true in the unrestricted default. */
  has: (id: ModuleId) => boolean;
  /** Does the install ship this feature? A string, so a screen may ask about one this build never heard of. */
  hasFeature: (feature: string) => boolean;
  /** The enabled modules, canonical order — `buildNav`'s 4th argument. `[]` while loading; every module in the unrestricted default. */
  modules: () => ModuleId[];
}

const CapabilitiesContext = createContext<CapabilitiesState>();

/**
 * The UNRESTRICTED install: every module enabled, every feature present.
 *
 * This is what `useCapabilities` answers OUTSIDE a provider and what the
 * provider falls back to when the query ERRORS past its retries — on purpose,
 * and documented here rather than thrown:
 *
 *   · 87 test files render screens without the shell. A hook that threw would
 *     make each of them mount a provider to test a table.
 *   · The API is the authority. `/api/<m>/*` for a disabled module answers 404
 *     MODULE_DISABLED regardless of what the UI shows, so the provider is a
 *     RESTRICTION the shell adds for a better screen, not a guard anything
 *     relies on. UI sugar fails OPEN; the gateway fails closed.
 *
 * `capabilities()` is null here: no manifest was received, and inventing one
 * would let a consumer mistake a guess for the gateway's answer.
 */
const UNRESTRICTED: CapabilitiesState = {
  capabilities: () => null,
  loading: () => false,
  has: () => true,
  hasFeature: () => true,
  modules: () => [...MODULE_IDS],
};

export interface CapabilitiesProviderProps {
  /** The one manifest call — typically `() => unwrap<Capabilities>(client.api.capabilities.$get())`. */
  fetchCapabilities: () => Promise<Capabilities>;
}

/**
 * One query over `GET /api/capabilities`, five-minute stale time (the manifest
 * is an install-time constant; a change is a deploy), two retries. Mounts
 * inside `SessionProvider` and outside the router, so the shell can wait for
 * both before it draws a sidebar.
 */
export function CapabilitiesProvider(props: ParentProps<CapabilitiesProviderProps>): JSX.Element {
  const q = useQuery(() => ({
    queryKey: CAPABILITIES_QUERY_KEY,
    queryFn: () => props.fetchCapabilities(),
    staleTime: 5 * 60_000,
    retry: (count) => count < 2,
  }));

  const capabilities = () => q.data ?? null;
  const state: CapabilitiesState = {
    capabilities,
    loading: () => q.isPending,
    has: (id) => {
      const c = capabilities();
      return c ? capsHasModule(c, id) : q.isError;
    },
    hasFeature: (feature) => {
      const c = capabilities();
      return c ? capsHasFeature(c, feature) : q.isError;
    },
    modules: () => capabilities()?.modules ?? (q.isError ? [...MODULE_IDS] : []),
  };

  return <CapabilitiesContext.Provider value={state}>{props.children}</CapabilitiesContext.Provider>;
}

/**
 * The install's capabilities. Does NOT throw outside a provider — it answers
 * the unrestricted install instead (see UNRESTRICTED for why that is the
 * honest default and not a foot-gun).
 */
export function useCapabilities(): CapabilitiesState {
  return useContext(CapabilitiesContext) ?? UNRESTRICTED;
}

/** Render gate: children only when the install enables the module. */
export function HasModule(props: ParentProps<{ id: ModuleId; fallback?: JSX.Element }>): JSX.Element {
  const caps = useCapabilities();
  return (
    <Show when={caps.has(props.id)} fallback={props.fallback}>
      {props.children}
    </Show>
  );
}

/** Render gate: children only when the install ships the feature. */
export function HasFeature(props: ParentProps<{ id: string; fallback?: JSX.Element }>): JSX.Element {
  const caps = useCapabilities();
  return (
    <Show when={caps.hasFeature(props.id)} fallback={props.fallback}>
      {props.children}
    </Show>
  );
}
